import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Hotel, Users, Calendar, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LAPackagesPage = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kuotaModal, setKuotaModal] = useState(false);

  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/packages/`, { params: { package_type: 'la' } });
      setPackages(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching LA packages:', error);
      }
      toast.error('Gagal memuat paket LA. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold font-['Outfit'] mb-4">Paket LA</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Paket dengan pilihan hotel dan fasilitas yang sudah diatur travel partner
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Memuat paket LA...</p>
          </div>
        )}

        {/* Empty */}
        {!loading && packages.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">Belum ada paket LA tersedia.</p>
            <p className="text-sm text-muted-foreground">Paket akan ditampilkan setelah admin menambahkan paket LA.</p>
            <Button variant="outline" className="mt-4 rounded-full" onClick={() => navigate('/umrah-selection')}>
              Kembali ke Pilihan Umrah
            </Button>
          </div>
        )}

        {/* Packages Grid */}
        {!loading && packages.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.map((pkg) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Card className="h-full flex flex-col hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-border rounded-2xl overflow-hidden">
                  {pkg.images && pkg.images.length > 0 && (
                    <div className="h-48 overflow-hidden">
                      <img src={pkg.images[0]} alt={pkg.package_name} className="w-full h-full object-cover" />
                    </div>
                  )}

                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-emerald-500 hover:bg-emerald-600">LA</Badge>
                      <Badge variant="outline" className="bg-accent/10 text-accent border-accent">
                        Rp {pkg.price.toLocaleString('id-ID')}
                      </Badge>
                    </div>
                    <CardTitle className="font-['Outfit'] text-xl">{pkg.package_name}</CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-2">
                      <Calendar className="h-4 w-4" />
                      {pkg.duration_days} Hari
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-1 space-y-3">
                    {pkg.hotel && (
                      <div className="flex items-start gap-2 text-sm">
                        <Hotel className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{pkg.hotel.name}</span>
                      </div>
                    )}
                    <div className="flex items-start gap-2 text-sm">
                      <Users className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">
                        Kuota: {pkg.available_quota}/{pkg.quota} tersedia
                      </span>
                    </div>
                    {pkg.includes && pkg.includes.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        Termasuk: {pkg.includes.slice(0, 3).join(', ')}{pkg.includes.length > 3 ? '...' : ''}
                      </div>
                    )}
                  </CardContent>

                  <CardFooter>
                    <Link to={`/packages/${pkg.id}`} className="w-full">
                      <Button className="w-full rounded-full bg-primary hover:bg-primary/90">
                        Lihat Detail
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Back */}
        <div className="text-center mt-12">
          <Button variant="outline" className="rounded-full" onClick={() => navigate('/umrah-selection')}>
            ← Kembali ke Pilihan Umrah
          </Button>
        </div>
      </div>

      {/* Kuota Modal */}
      {kuotaModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setKuotaModal(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-3xl h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold font-['Outfit']">Informasi Sisa Kuota</h2>
              <button onClick={() => setKuotaModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1">
              <iframe src="https://bisa.work/kuota" className="w-full h-full border-0" title="Informasi Sisa Kuota" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LAPackagesPage;
